/**
 * DailyLimitDialog — iter1118c
 *
 * Глобален модал, който се показва когато бекендът върне 429 с
 * съобщение за достигнат дневен лимит от „Подарък" код. Дава
 * бутон „Напомни ми утре", който насрочва писмо за следващата сутрин.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Zap, Clock, CheckCircle2 } from "lucide-react";

// Глобален event — всеки axios 429 с „дневен лимит" издава go-event, който слуша този компонент.
const DAILY_LIMIT_EVENT = "nutramilo:daily-limit-hit";

export function emitDailyLimit() {
  window.dispatchEvent(new CustomEvent(DAILY_LIMIT_EVENT));
}

export default function DailyLimitDialog() {
  const [open, setOpen] = useState(false);
  const [scheduled, setScheduled] = useState(false);
  const [working, setWorking] = useState(false);
  const [remindAt, setRemindAt] = useState(null);

  const onEvent = useCallback(() => {
    setOpen(true);
    setScheduled(false);
  }, []);

  useEffect(() => {
    window.addEventListener(DAILY_LIMIT_EVENT, onEvent);
    return () => window.removeEventListener(DAILY_LIMIT_EVENT, onEvent);
  }, [onEvent]);

  const scheduleReminder = async () => {
    const API = (typeof window !== "undefined" && window.__NUTRAMILO_API)
      || `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
    setWorking(true);
    try {
      const { data } = await axios.post(`${API}/gift/remind-tomorrow`, {});
      setRemindAt(data.remind_at);
      setScheduled(true);
    } catch { /* тих провал */ }
    setWorking(false);
  };

  const remindAtLocal = remindAt
    ? new Date(remindAt).toLocaleString("bg-BG", { dateStyle: "long", timeStyle: "short" })
    : "утре сутринта";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md" data-testid="daily-limit-dialog">
        <div className="text-center py-2">
          <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
            <Zap className="w-7 h-7 text-amber-600" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">
            Днес използвахте всички кредити от подаръка
          </h3>
          <p className="text-sm text-gray-600 leading-relaxed mb-6">
            Дневният лимит се възстановява всяка сутрин. Връщайте се утре,
            за да продължите изследването в собственото си темпо.
          </p>

          {scheduled ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4" data-testid="daily-limit-scheduled">
              <div className="flex items-center justify-center gap-2 text-emerald-800 mb-1">
                <CheckCircle2 className="w-5 h-5" />
                <strong className="text-sm">Напомнянето е насрочено</strong>
              </div>
              <p className="text-xs text-emerald-700">
                Ще получите писмо на {remindAtLocal}.
              </p>
            </div>
          ) : (
            <Button
              onClick={scheduleReminder}
              disabled={working}
              className="w-full h-11 bg-gradient-to-r from-amber-500 to-orange-500 hover:opacity-90 text-white"
              data-testid="daily-limit-remind-btn"
            >
              <Clock className="w-4 h-4 mr-2" />
              {working ? "Моля изчакайте…" : "Напомни ми утре"}
            </Button>
          )}

          <button
            type="button"
            onClick={() => setOpen(false)}
            className="mt-3 text-xs text-gray-500 hover:text-gray-700"
            data-testid="daily-limit-close"
          >
            Затвори
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
